import { createFileRoute, Link, Navigate, Outlet, useMatchRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMe } from '@/api/me'
import { needsWelcome } from '@/api/onboarding'
import { useWorkspace } from '@/api/workspace'
import { AppShell } from '@/components/app-shell/app-shell'
import { AppShellSkeleton } from '@/components/app-shell/app-shell-skeleton'
import { FullPageMessage } from '@/components/full-page-message'
import { Button } from '@/components/ui/button'
import { setSentryContext } from '@/lib/sentry'

export const Route = createFileRoute('/w/$slug')({
  component: WorkspaceLayout,
})

function WorkspaceLayout() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const me = useMe()
  // The server decides whether this workspace exists for this user, and records the visit.
  const workspace = useWorkspace(slug)
  const matchRoute = useMatchRoute()
  const onWelcome = Boolean(matchRoute({ to: '/w/$slug/welcome', params: { slug } }))
  const userId = me.data?.user.id

  useEffect(() => setSentryContext(userId, slug), [userId, slug])

  if (me.isPending || workspace.isPending) return <AppShellSkeleton />
  if (me.isError || !me.data) return <Navigate to="/login" replace />

  // Same screen whether the workspace doesn't exist or the user isn't a member of it.
  if (workspace.isError || !workspace.data) {
    return (
      <FullPageMessage
        title={t('notFound.title')}
        body={t('notFound.body')}
        action={
          <Button asChild variant="outline">
            <Link to="/login">{t('notFound.backToLogin')}</Link>
          </Button>
        }
      />
    )
  }

  // The Owner of a Workspace that hasn't picked a track yet starts on the Welcome screen
  // (09 Onboarding), which is a full page without the shell.
  if (needsWelcome(workspace.data) && !onWelcome) {
    return <Navigate to="/w/$slug/welcome" params={{ slug }} replace />
  }
  if (onWelcome) return <Outlet />

  return (
    <AppShell
      slug={slug}
      workspaceName={workspace.data.name}
      role={workspace.data.role}
      memberships={me.data.memberships}
      user={me.data.user}
    >
      <Outlet />
    </AppShell>
  )
}
